import { Attrs, MinzeElement, Reactive } from 'minze'
import { parseInlineStyle } from '../../../modules/utils'
import { Font } from '../../../constants'
import { Locale } from '../../../modules/locale'

Locale.getInstance().registerKeySet('fr', 'lvcButton', {
  loading: 'Chargement...',
})
Locale.getInstance().registerKeySet('en', 'lvcButton', {
  loading: 'Loading...',
})

interface BtnPreset {
  borderRadius: string
  borderTop: string
  borderBottom: string
  padding: string
  height: string
}

const btnPresets: Record<string, BtnPreset> = {
  profile: {
    borderRadius: '0px',
    borderTop: '1px solid #1E007D',
    borderBottom: 'none',
    padding: '16px 0',
    height: '64px',
  },
  profileLastButton: {
    borderRadius: '0px',
    borderTop: '1px solid #1E007D',
    borderBottom: '1px solid #1E007D',
    padding: '16px 0',
    height: '64px',
  },
}

export class LvcButton extends MinzeElement {
  reactive: Reactive = [
    ['background', '#1E007D'],
    ['color', '#fff'],
  ]
  attrs: Attrs = [
    'type',
    'display',
    'flex',
    ['text-align', 'center'],
    ['border', 'none'],
    ['border-radius', '99px'],
    ['border-bottom', ''],
    ['border-top', ''],
    ['border-left', ''],
    ['border-right', ''],
    ['margin-bottom', ''],
    ['margin-left', ''],
    ['width', '100%'],
    ['height', '56px'],
    ['font-size', '14px'],
    ['font-weight', Font.weight.medium],
    ['background', '#1E007D'],
    ['color', '#fff'],
    ['padding', '0.5rem 1rem'],
    ['background'],
    ['color', '#fff'],
    ['theme'],
    ['bg'],
    ['inner-style', ''],
    ['inner-style', ''],
    'enable-hover-box-shadow',
    'enable-hover-translate',
    'enable-hover-pulse',
    'align-items',
    'justify-content',
    'is-mobile-floating',
    'disabled',
    'color',
    ['loading', false],
  ]

  static observedAttributes = [
    'loading',
    'theme',
    'color',
    'bg',
    'background',
    'disabled',
    'inner-style',
  ]

  onAttributeChange = (name: string, oldValue: string, newValue: string) => {
    if (name === 'theme') {
      if (newValue === 'dark') {
        this.background = '#1E007D'
        this.color = '#fff'
      }

      if (newValue === 'transparent') {
        this.background = this.bg ? this.bg : 'transparent'
        this.color = '#1E007D'
      }

      if (newValue === 'selectedFilter') {
        this.background = '#BFF0FF'
        this.color = '#1E007D'
      }
    }
  }

  get btnPreset(): BtnPreset | undefined {
    const preset = this.getAttribute('btn-preset')
    return preset ? btnPresets[preset as keyof typeof btnPresets] : undefined
  }

  html = () => `
    <button part="button" class="button" type="${this.type}" ${this.disabled ? 'disabled' : ''}>

    ${this.loading ? `<div class="loader-wrapper"><div class="loader"></div><div class="loader-text">${Locale.getKey('lvcButton.loading')}</div></div>` : `<slot></slot>`}
    </button>
  `

  css = () => {
    this.heightValue = this.height ? this.height : '56px'
    this.borderRadiusValue = this.borderRadius ? this.borderRadius : '99px'

    return /*css*/ `
      :host {
        ${this.flex ? `flex: ${this.flex};` : ''}
        pointer-events: ${this.loading ? 'none' : 'auto'}
      }
      .loader-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
      }
      .loader-text {
        font-size: 12px;
      }
      .loader {
        flex-shrink: 0;
        border: 2px solid #ffffff;
        border-top: 2px solid transparent;
        border-radius: 50%;
        width: 16px;
        height: 16px;
        animation: spin 1s linear infinite;
      }
      @keyframes spin {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }

      .button {
        font-family: ${Font.family};
        width: ${this.width};
        ${this.textAlign ? `text-align: ${this.textAlign};` : ''}
        background: ${this.bg || this.background};
        color: ${this.color};
        font-size: ${this.fontSize};
        font-weight: ${this.fontWeight || Font.weight.medium};
        border: ${this.border || 'none'};
        ${this.borderRadiusValue ? `border-radius: ${this.borderRadiusValue};` : ''}
        ${this.borderRadius ? `border-radius: ${this.btnPreset?.borderRadius || this.borderRadius};` : ''}
        padding: ${this.btnPreset?.padding || this.padding};
        cursor: ${this.disabled ? 'not-allowed' : 'pointer'};
        transition: filter 0.3s ease-in-out, box-shadow 0.3s ease, transform 0.3s ease;
        ${this.heightValue ? `height: ${this.heightValue};` : ''}
        ${this.height ? `height: ${this.btnPreset?.height || this.height};` : ''}
        border-bottom: ${this.btnPreset?.borderBottom || this.borderBottom};
        border-top: ${this.btnPreset?.borderTop || this.borderTop};
        border-left: ${this.borderLeft};
        border-right: ${this.borderRight};
        margin-bottom: ${this.marginBottom};
        margin-left: ${this.marginLeft};
        ${this.display ? `display: ${this.display};` : ''}
        ${this.alignItems ? `align-items: ${this.alignItems};` : ''}
        ${this.justifyContent ? `justify-content: ${this.justifyContent};` : ''}
        ${this.enableHoverPulse ? `animation: pulsate 0.6s infinite; ` : ``}
        animation-play-state: paused; 
        transform: perspective(1px) translateZ(0);
        backface-visibility: hidden;
      }
      .button > * {
        font-family: ${Font.family};
        font-size: ${this.fontSize};
        font-weight: ${this.fontWeight};
      }
      ${
        this.isMobileFloating
          ? `
        @media only screen and (max-width: 991px) {
          .button {
            box-shadow: 0px 14px 20px 0px rgba(30, 0, 125, 0.25);
          }
        }
        `
          : ''
      }

      @keyframes pulsate {
        0% { transform: scale(1); }
        50% { transform: scale(1.03); } /* Scale up to 5% larger */
        100% { transform: scale(1); }
      }
      
      .button:hover {
        ${this.enableHoverBoxShadow && !this.isMobileFloating ? 'box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25), 0px 14px 20px 0px rgba(30, 0, 125, 0.18);' : ''}
        ${this.enableHoverTranslate && !this.isMobileFloating ? `transform: translateX(5px);` : ''}	
        ${this.enableHoverPulse ? `animation-play-state: running;` : ``}
        ${this.enableHoverPulse ? `filter: brightness(0.9);` : ``} /* Adjust brightness during pulsation */
        ${this.enableHoverPulse ? `background-size: 110%;` : ``} /* Scale up background image */
      }
      ${parseInlineStyle(this.innerStyle as string)}
    `
  }
}
